import { createContext, useContext, useEffect, ReactNode, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Language = 'he' | 'en';

interface LanguageContextType {
  language: Language;
  isRTL: boolean;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  
  // Use state to track language changes and trigger re-renders
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('i18nextLng');
    if (stored === 'en' || stored === 'he') return stored;
    return (i18n.language as Language) || 'he';
  });
  
  const isRTL = language === 'he';

  // Listen to i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLang = lng as Language;
      setLanguageState(newLang);
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    // Update document direction and lang attribute
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'he' ? 'en' : 'he';
    localStorage.setItem('i18nextLng', newLang);
    i18n.changeLanguage(newLang);
  }, [language, i18n]);

  const setLanguage = useCallback((lang: Language) => {
    localStorage.setItem('i18nextLng', lang);
    i18n.changeLanguage(lang);
  }, [i18n]);

  return (
    <LanguageContext.Provider value={{ language, isRTL, toggleLanguage, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
